import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { observer } from "mobx-react";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { Loader2 } from "lucide-react";
import { useRootStore } from "@/state/RootStore";
import { markReferralExpiryPopupSeen } from "@/api/fetch";
import { captureEvent } from "@/utils/posthog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { useRedemptionPresentationBlocker } from "@/components/redemption/RedemptionPresentationContext";

const TrialExpiredModal = observer(() => {
  const { t } = useTranslation();
  const store = useRootStore();
  const navigate = useNavigate();
  const flagEnabled = useFeatureFlagEnabled("refer-a-friend") === true;

  const [open, setOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const shouldRender =
    flagEnabled && store.mainAppStore.shouldShowReferralExpiryPopup();
  useRedemptionPresentationBlocker(
    "referral-trial-expired",
    Boolean(shouldRender && open),
  );

  const shownRef = useRef(false);
  useEffect(() => {
    if (shouldRender && open && !shownRef.current) {
      shownRef.current = true;
      captureEvent("referral_trial_expired_popup_shown", { platform: "web" });
    }
  }, [shouldRender, open]);

  if (!shouldRender) return null;

  const handleSeePlans = async () => {
    setIsLoading(true);
    try {
      await markReferralExpiryPopupSeen();
    } catch {
      // Server flag is best-effort; navigate regardless.
    } finally {
      setIsLoading(false);
      setOpen(false);
      navigate("/paywall");
    }
  };

  const handleDismiss = async () => {
    setIsLoading(true);
    try {
      await markReferralExpiryPopupSeen();
      await store.mainAppStore.checkPurchaseState();
    } catch {
      // Best-effort; close regardless so the user isn't trapped.
    } finally {
      setIsLoading(false);
      setOpen(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && handleDismiss()}>
      <DialogContent className="sm:max-w-sm rounded-3xl p-0 overflow-hidden bg-white">
        <div className="px-6 py-8 text-center">
          <DialogHeader>
            <DialogTitle className="text-center text-base font-bold text-[#232323]">
              {t("referral.expired.title")}
            </DialogTitle>
            <DialogDescription className="mx-auto mt-3 max-w-[18rem] text-center text-sm font-normal leading-6 text-[#666666]">
              {t("referral.expired.body")}
            </DialogDescription>
          </DialogHeader>
          <Button
            type="button"
            onClick={handleSeePlans}
            disabled={isLoading}
            className={cn(
              "mt-7 h-auto w-full rounded-full bg-[var(--dp-brand-button)] py-3.5 text-base font-semibold text-white hover:bg-[var(--dp-brand-hover)]",
              isLoading && "bg-gray-200 hover:bg-gray-200 text-black",
            )}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t("referral.expired.cta")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
});

export default TrialExpiredModal;
